import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Heart, Share2, MessageCircle, Bookmark } from "lucide-react";

/**
 * ReelPlayer
 * Simulates a vertical 9:16 Instagram/TikTok style reel.
 * props:
 *  - reel: full reel object (scenes with image_base64, headline, subtitle, hook, cta, business_name)
 *  - autoPlay: boolean
 *  - compact: boolean (smaller frame)
 */
const DEFAULT_SCENE_DURATION_MS = 5000;

const ReelPlayer = ({ reel, autoPlay = true, compact = false }) => {
  const [currentScene, setCurrentScene] = useState(0);
  const [playing, setPlaying] = useState(autoPlay);
  const [muted, setMuted] = useState(true);
  const timeoutRef = useRef(null);
  const scenes = reel?.scenes || [];

  // Keep currentScene within bounds when scenes array size changes (edit/delete/add)
  useEffect(() => {
    if (scenes.length === 0) return;
    setCurrentScene((prev) => (prev >= scenes.length ? 0 : prev));
  }, [scenes.length]);

  // Per-scene auto-advance using each scene's own duration_ms
  useEffect(() => {
    if (!playing || scenes.length === 0) return;
    const current = scenes[Math.min(currentScene, scenes.length - 1)];
    const dur = current?.duration_ms || DEFAULT_SCENE_DURATION_MS;
    timeoutRef.current = setTimeout(() => {
      setCurrentScene((prev) => (prev + 1) % scenes.length);
    }, dur);
    return () => clearTimeout(timeoutRef.current);
  }, [playing, scenes.length, currentScene, scenes]);

  if (!scenes.length) return null;

  const safeIndex = Math.min(currentScene, scenes.length - 1);
  const scene = scenes[safeIndex];
  const progress = ((safeIndex + 1) / scenes.length) * 100;
  const currentDurationMs = scene?.duration_ms || DEFAULT_SCENE_DURATION_MS;
  const primary = reel.brand_color_primary || "#FF4400";
  const secondary = reel.brand_color_secondary || "#FF6633";

  // Honor per-scene text_position and text_size coming from the Scene Editor.
  // "auto" means the canvas renderer picks the best band at export time
  // by sampling the background luminance; for the in-app preview we
  // approximate it as "center" (the auto position is only visible in the
  // exported MP4 where the heavy pixel analysis runs).
  const rawTextPosition = scene?.text_position || "center";
  const textPosition = rawTextPosition === "auto" ? "center" : rawTextPosition;
  const textSize = scene?.text_size || "normal";
  const headlineSizeClass =
    textSize === "small" ? "text-xl" : textSize === "large" ? "text-4xl" : "text-3xl";
  const subtitleSizeClass =
    textSize === "small" ? "text-[10px]" : textSize === "large" ? "text-sm" : "text-xs";

  return (
    <div
      data-testid="reel-player"
      className={`relative overflow-hidden rounded-md bg-black border border-zinc-800 shadow-2xl ${
        compact ? "w-full max-w-[280px]" : "w-full max-w-[360px]"
      }`}
      style={{ aspectRatio: "9/16" }}
    >
      {/* Scene Image / Video */}
      <div className="absolute inset-0">
        {scenes.map((s, i) => {
          const visible = i === safeIndex;
          if (s.video_id) {
            return (
              <video
                key={`v-${i}-${s.video_id}`}
                src={`${process.env.REACT_APP_BACKEND_URL}/api/reels/uploads/video/${s.video_id}`}
                muted
                playsInline
                autoPlay={visible && playing}
                loop
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                style={{
                  opacity: visible ? 1 : 0,
                  transition: "opacity 700ms ease-out",
                }}
              />
            );
          }
          return (
            <img
              key={`${i}-${(s.image_base64 || "").slice(0, 16)}`}
              src={`data:image/png;base64,${s.image_base64}`}
              alt={`Scene ${i + 1}`}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "scale(1.04)" : "scale(1)",
                transition: "opacity 700ms ease-out, transform 3000ms ease-out",
              }}
            />
          );
        })}
      </div>

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/80 pointer-events-none" />

      {/* Top progress bars */}
      <div className="absolute top-2 left-2 right-2 flex gap-1 z-20">
        {scenes.map((_, i) => (
          <div key={i} className="flex-1 h-[2px] bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white"
              style={{
                width: i < safeIndex ? "100%" : i === safeIndex ? `${progress}%` : "0%",
                transition: i === safeIndex ? `width ${currentDurationMs}ms linear` : "none",
              }}
            />
          </div>
        ))}
      </div>

      {/* Top: business badge */}
      <div className="absolute top-6 left-3 right-3 z-10 flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-black text-white font-display overflow-hidden"
          style={{ backgroundColor: primary }}
        >
          {reel.brand_logo ? (
            <img src={`data:image/png;base64,${reel.brand_logo}`} alt="logo" className="w-full h-full object-cover" />
          ) : (
            reel.business_name?.[0]?.toUpperCase() || "S"
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white text-xs font-bold truncate font-display" data-testid="reel-business-name">
            {reel.business_name}
          </div>
          <div className="text-white/60 text-[10px] truncate font-mono uppercase tracking-wider">
            {reel.music_mood}
          </div>
        </div>
        <button
          onClick={() => setMuted((m) => !m)}
          data-testid="reel-mute-toggle"
          className="w-7 h-7 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white hover:bg-black/60 transition-colors"
        >
          {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Center: text overlay. Positions MUST match the canvas renderer
          in clientRender.js (composeSceneFrameBlob) so the in-app
          preview is faithful to the exported MP4. The canvas places
          the headline-centric block ~9% above pure vertical center
          (the -80px shift on a 854px tall canvas) to leave room for
          subtitle + bottom CTA. We approximate that with a top/bottom
          window biased upward. */}
      <div
        className="absolute left-0 right-0 px-6 z-10"
        style={
          textPosition === "top"
            ? { top: "5.7%" }     // matches TOP_RESERVED ≈ 5.7%
            : textPosition === "bottom"
            ? { bottom: "12%" }   // ~2% above the CTA pill at bottom:10%
            : { top: "26%", bottom: "34%", display: "flex", alignItems: "center", justifyContent: "center" }
                                  // upper-center band — same vertical
                                  // anchor as the canvas (-80px offset
                                  // from the geometric center)
        }
      >
        <div key={`${safeIndex}-${scene.headline}`} className="text-center slide-up w-full">
          {/* Scene label pill — hidden on the title card (scene 1) since
              the reel title + hook themselves carry the message. */}
          {safeIndex > 0 && (
            <div
              className="inline-block px-2 py-1 text-white text-[10px] font-mono uppercase tracking-widest font-bold mb-3"
              style={{ backgroundColor: primary }}
            >
              Scene {safeIndex + 1}
            </div>
          )}
          <h2 className={`font-display font-black text-white ${headlineSizeClass} leading-[1.05] tracking-tighter drop-shadow-2xl mb-2`}>
            {safeIndex === 0 ? ((scene.headline || "").trim() || reel.title) : scene.headline}
          </h2>
          {/* On the title card show the hook as subtitle; otherwise show
              the per-scene subtitle. Falls back gracefully if either is empty.
              Honour user-edited scene text first — only fall back to
              reel.hook when the scene's own subtitle is empty. */}
          {(safeIndex === 0 ? ((scene.subtitle || "").trim() || reel.hook) : scene.subtitle) && (
            <p className={`text-white/90 ${subtitleSizeClass} font-body font-medium max-w-[240px] mx-auto leading-snug drop-shadow-lg`}>
              {safeIndex === 0 ? ((scene.subtitle || "").trim() || reel.hook) : scene.subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Right: action buttons (TikTok style) */}
      <div className="absolute right-2 bottom-24 flex flex-col items-center gap-4 z-10">
        {[
          { Icon: Heart, label: "12.4k" },
          { Icon: MessageCircle, label: "328" },
          { Icon: Bookmark, label: "" },
          { Icon: Share2, label: "" },
        ].map(({ Icon, label }, i) => (
          <div key={i} className="flex flex-col items-center">
            <div className="w-9 h-9 rounded-full bg-black/30 backdrop-blur flex items-center justify-center text-white">
              <Icon className="w-4 h-4" />
            </div>
            {label && <span className="text-[10px] text-white/80 mt-0.5 font-mono">{label}</span>}
          </div>
        ))}
      </div>

      {/* Bottom CTA — anchored ~10% from the bottom (lowered per user
          feedback). Suppressed entirely when reel.show_cta === false. */}
      {reel.show_cta !== false && (
        <div className="absolute bottom-[10%] left-3 right-14 z-10">
          <div
            className="text-white px-3 py-2 rounded-sm font-display font-bold text-xs text-center tracking-wide"
            style={{ background: `linear-gradient(90deg, ${primary}, ${secondary})` }}
            data-testid="reel-cta"
          >
            {reel.cta}
          </div>
        </div>
      )}

      {/* Play/Pause center tap */}
      <button
        onClick={() => setPlaying((p) => !p)}
        data-testid="reel-play-toggle"
        className="absolute inset-0 z-0 flex items-center justify-center"
        aria-label={playing ? "Pause" : "Play"}
      >
        {!playing && (
          <div className="w-14 h-14 rounded-full bg-black/50 backdrop-blur flex items-center justify-center">
            <Play className="w-6 h-6 text-white fill-white" />
          </div>
        )}
      </button>
    </div>
  );
};

export default ReelPlayer;
